import {styled} from '@linaria/react';
import React, {ComponentType, PropsWithChildren, Ref, forwardRef} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES} from '../../../constants/typography';
import {AriaButtonLinkType} from '../../../types/AriaButtonLink';
import {Ripple} from '../Ripple/Ripple';
import {UnstyledLink} from '../UnstyledLink/UnstyledLink';
import {getBackgroundColor, getFontColor} from './helpers';

export type ButtonType = 'primary' | 'secondary' | HTMLButtonElement['type'];

export interface ButtonProps {
  className?: string;
  style?: React.CSSProperties;
  grow?: boolean;
  type?: ButtonType;
  href?: string;
  disableShadow?: boolean;
  disableRipple?: boolean;
  withBorder?: boolean;
  onClick?: React.MouseEventHandler;
  disabled?: boolean;
  ['data-testid']?: string;
  openExternalLinkInNewTab?: boolean;
  openLinkInNewTab?: boolean;
  tabIndex?: number;
  form?: string;
}

export const Button = forwardRef<
  HTMLElement,
  PropsWithChildren<ButtonProps & AriaButtonLinkType>
>(function Button(
  {
    href,
    onClick,
    disabled,
    'data-testid': testId,
    openExternalLinkInNewTab,
    openLinkInNewTab,
    disableShadow = false,
    withBorder,
    grow,
    'aria-label': ariaLabel,
    ...otherProps
  },
  ref,
) {
  if (href && !disabled) {
    const {className, style, ...rest} = otherProps;
    return (
      <UnstyledLink
        ref={ref as Ref<HTMLAnchorElement>}
        to={href}
        onClick={onClick}
        data-testid={testId}
        openExternalLinkInNewTab={openExternalLinkInNewTab}
        openLinkInNewTab={openLinkInNewTab}
        className={className}
        style={style}
        aria-label={ariaLabel}
      >
        <Ripple
          as={StyledButtonSpan}
          data-disable-shadow={disableShadow}
          data-with-border={withBorder}
          data-grow={grow}
          {...rest}
        />
      </UnstyledLink>
    );
  }

  return (
    <Ripple
      ref={ref}
      as={StyledButton as ComponentType<PropsWithChildren>}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={ariaLabel}
      data-disable-shadow={disableShadow}
      data-with-border={withBorder}
      data-grow={grow}
      {...otherProps}
    />
  );
});

const buttonShadow = `
  box-shadow: 0 3px 5px 0 transparent;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  &:hover,
  &:focus {
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px 0px rgba(0, 0, 0, 0.14),
      0px 1px 10px 0px rgba(0, 0, 0, 0.12);

    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0px);
    box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2),
      0px 8px 10px 1px rgba(0, 0, 0, 0.14),
      0px 3px 14px 2px rgba(0, 0, 0, 0.12);
   }
`;

const buttonStyles = `
  display: inline-block;
  cursor: pointer;
  outline: none;
  margin: 0;
  font-size: ${FONT_SIZES.px14};
  font-weight: bold;
  text-align: center;
  border-radius: 4px;
  border: 0;
  text-decoration: none;
  transition: box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  padding: 16px 40px;

  &[data-grow="true"] {
    padding: 16px 80px;
  }

  &[data-with-border="true"] {
    border: 1px solid ${COLORS.Grey3};
  }

  /* we have shadows unless we explicitly specify 'false' */
  &[data-disable-shadow="false"] {
    ${buttonShadow}
  }

  &[disabled] {
    cursor: auto;
    filter: opacity(0.6) grayscale(0.85) brightness(1.1);
  }
`;

export const StyledButton = styled.button<ButtonProps>`
  ${buttonStyles}

  background-color: ${({type}) => getBackgroundColor(type)};
  color: ${({type}) => getFontColor(type)};

  --firefox-outline: 2px auto Highlight;
  --outline: 2px auto -webkit-focus-ring-color;

  &:focus-visible {
    outline: var(--firefox-outline);
    outline: var(--outline);
  }
`;

const StyledButtonSpan = styled.span<ButtonProps>`
  ${buttonStyles}

  background-color: ${({type}) => getBackgroundColor(type)};
  color: ${({type}) => getFontColor(type)};

  --firefox-outline: 2px auto Highlight;
  --outline: 2px auto -webkit-focus-ring-color;

  &:focus-visible {
    outline: var(--firefox-outline);
    outline: var(--outline);
    outline-color: ${props =>
      props.type === 'primary' ? `${COLORS.White}` : 'Highlight'};
  }
`;
